import { useMemo, useState } from "react";
import "./products.css";
import { useNavigate, Link } from "react-router-dom";
import { useGetAllProductQuery, useDeleteProductMutation } from "../../services/productApi";
import { Inventory2Outlined, Add } from "@mui/icons-material";
import {
  AdminPageHeader,
  AdminToolbar,
  AdminTable,
  AdminBadge,
  AdminButton,
  AdminRowActions,
  AdminEmptyState,
  stockVariant,
  stockLabel,
  useConfirm,
  useToast,
} from "../../components/ui";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "name_asc", label: "Name (A-Z)" },
  { value: "name_desc", label: "Name (Z-A)" },
  { value: "price_asc", label: "Price (Low-High)" },
  { value: "price_desc", label: "Price (High-Low)" },
  { value: "stock_asc", label: "Stock (Low-High)" },
  { value: "stock_desc", label: "Stock (High-Low)" },
];

function sortProducts(list, sortBy) {
  const sorted = list.slice();
  switch (sortBy) {
    case "oldest":
      return sorted.sort((a, b) => a.id - b.id);
    case "name_asc":
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case "name_desc":
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
    case "price_asc":
      return sorted.sort((a, b) => (a.variants?.[0]?.price ?? 0) - (b.variants?.[0]?.price ?? 0));
    case "price_desc":
      return sorted.sort((a, b) => (b.variants?.[0]?.price ?? 0) - (a.variants?.[0]?.price ?? 0));
    case "stock_asc":
      return sorted.sort((a, b) => (a.variants?.[0]?.stock_quantity ?? 0) - (b.variants?.[0]?.stock_quantity ?? 0));
    case "stock_desc":
      return sorted.sort((a, b) => (b.variants?.[0]?.stock_quantity ?? 0) - (a.variants?.[0]?.stock_quantity ?? 0));
    case "newest":
    default:
      return sorted.sort((a, b) => b.id - a.id);
  }
}

function Products() {
  const navigate = useNavigate();
  const response = useGetAllProductQuery();
  const [deleteProduct] = useDeleteProductMutation();
  const confirm = useConfirm();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [selected, setSelected] = useState(new Set());

  const rows = useMemo(() => {
    const source = response.data || [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? source.filter((p) =>
          p.name.toLowerCase().includes(term) ||
          (p.category && p.category.name.toLowerCase().includes(term)) ||
          (p.description && p.description.toLowerCase().includes(term))
        )
      : source;
    return sortProducts(filtered, sortBy);
  }, [response.data, search, sortBy]);

  const handleDelete = async (product) => {
    const ok = await confirm({
      title: "Delete product",
      message: `Delete "${product.name}"? This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const res = await deleteProduct(product.id);
    if (res.error) {
      toast.error(res.error.data?.errors?.[0] || "Could not delete this product.");
    } else {
      toast.success("Product deleted.");
      selected.delete(product.id);
      setSelected(new Set(selected));
    }
  };

  const handleBulkDelete = async () => {
    const ok = await confirm({
      title: "Delete products",
      message: `Delete ${selected.size} selected product${selected.size === 1 ? "" : "s"}? This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const results = await Promise.all([...selected].map((id) => deleteProduct(id)));
    const failed = results.filter((r) => r.error).length;
    if (failed > 0) toast.error(`${failed} product${failed === 1 ? "" : "s"} could not be deleted.`);
    else toast.success("Selected products deleted.");
    setSelected(new Set());
  };

  const columns = [
    {
      key: "name",
      header: "Product",
      sortable: false,
      render: (p) => {
        const mainImage = p.images?.[0] ? `${API_BASE}${p.images[0].image}` : "";
        return (
          <div className="productRow">
            <div className="productRow__thumb">
              {mainImage ? <img src={mainImage} alt="" /> : <Inventory2Outlined className="productRow__thumb-fallback" />}
            </div>
            <div className="productRow__text">
              <span className="productRow__name">{p.name}</span>
              <span className="productRow__sku">ID: {p.id}</span>
            </div>
          </div>
        );
      },
    },
    { key: "category", header: "Category", render: (p) => p.category?.name || "—" },
    {
      key: "price",
      header: "Price",
      align: "right",
      render: (p) => (p.variants?.[0] ? `₹${p.variants[0].price}` : "—"),
    },
    {
      key: "stock",
      header: "Stock",
      render: (p) => {
        const variant = p.variants?.[0];
        if (!variant) return "—";
        return <AdminBadge variant={stockVariant(variant.stock_quantity)}>{stockLabel(variant.stock_quantity)}</AdminBadge>;
      },
    },
    {
      key: "is_active",
      header: "Status",
      render: (p) => {
        const active = Boolean(p.variants?.[0]?.is_active);
        return <AdminBadge variant={active ? "success" : "neutral"}>{active ? "Active" : "Inactive"}</AdminBadge>;
      },
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (p) => (
        <AdminRowActions
          label={p.name}
          onEdit={() => navigate(`/admin/product/edit/${p.id}`)}
          onDelete={() => handleDelete(p)}
        />
      ),
    },
  ];

  if (response.isError) {
    return (
      <div>
        <AdminPageHeader title="Products" breadcrumbs={[{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Products" }]} />
        <AdminEmptyState
          icon={<Inventory2Outlined />}
          title="Couldn't load products"
          description="Something went wrong fetching the product list. Try refreshing the page."
        />
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Products"
        subtitle="Manage your product catalog and inventory."
        breadcrumbs={[{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Products" }]}
        primaryAction={
          <Link to="/admin/product/new">
            <AdminButton icon={<Add />}>New Product</AdminButton>
          </Link>
        }
      />
      <AdminToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search products by name, category or description..."
        filters={
          <select className="admin-field__control" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        }
      />
      <AdminTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id}
        isLoading={response.isLoading}
        emptyIcon={<Inventory2Outlined />}
        emptyTitle={search ? "No products match your search" : "No products yet"}
        emptyDescription={search ? "Try a different search term." : "Products you add will show up here."}
        emptyAction={
          !search && (
            <Link to="/admin/product/new">
              <AdminButton icon={<Add />}>New Product</AdminButton>
            </Link>
          )
        }
        selectable
        selectedKeys={selected}
        onSelectionChange={setSelected}
        bulkActionsBar={
          <AdminButton variant="danger" size="sm" onClick={handleBulkDelete}>
            Delete selected
          </AdminButton>
        }
        pageSize={10}
      />
    </div>
  );
}

export default Products;
